import React, {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
} from 'react';
import { useApolloClient, useQuery } from '@apollo/client';
import {
  Autocomplete,
  Box,
  Button,
  Card,
  CardContent,
  Checkbox,
  CircularProgress,
  FormControl,
  MenuItem,
  Select,
  Snackbar,
  TextField,
  Typography,
  Accordion,
  AccordionSummary,
  AccordionDetails,
} from '@mui/material';
import { styled, useTheme } from '@mui/material/styles';
import { keyframes } from '@emotion/react';
import AutoAwesomeIcon from '@mui/icons-material/AutoAwesome';
import CheckBoxIcon from '@mui/icons-material/CheckBox';
import CheckBoxOutlineBlankIcon from '@mui/icons-material/CheckBoxOutlineBlank';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import ExpandMoreIcon from '@mui/icons-material/ExpandMore';
import SendIcon from '@mui/icons-material/Send';
import palette from 'src/theme/palette';
import useBackendConnectors from 'src/hooks/useBackendConnectors';
import { useAccount } from 'src/providers/account/hook/useAccount';
import { useSidebar } from 'src/providers/sidebar/useSidebar';
import {
  GENERATE_DECK,
  GET_CUSTOM_REPORTS,
  GET_KPI_SECTIONS,
} from 'src/utils/gql/queries';
import type { CustomReport, KpiSection } from 'src/utils/gql/types';
import {
  buildDevApiHintJson,
  buildStudioExportPrompt,
  inferDeckLanguageFromChat,
} from './buildDeckDataContext';
import { buildKpiSectionPromptBlocks } from './buildKpiSectionPromptBlocks';

const drift = keyframes`
  0%, 100% { opacity: 0.35; transform: scale(1) translate(0, 0); }
  50% { opacity: 0.55; transform: scale(1.05) translate(2%, -1%); }
`;

const SLIDE_OPTIONS = [3, 5, 7, 10] as const;

type ChatRole = 'user' | 'assistant';

type ChatMessage = {
  id: string;
  role: ChatRole;
  content: string;
};

const PageRoot = styled(Box)(({ theme }) => ({
  minHeight: '100vh',
  width: '100%',
  boxSizing: 'border-box',
  padding: theme.spacing(2.5),
  position: 'relative',
  overflow: 'hidden',
  background: `linear-gradient(165deg, ${palette.primary.main}0a 0%, ${palette.background.default} 38%, #f8fafc 100%)`,
  [theme.breakpoints.down('sm')]: {
    padding: theme.spacing(1.5),
    marginTop: '60px',
  },
  [theme.breakpoints.up('sm')]: {
    marginLeft: '240px',
    width: 'calc(100% - 240px)',
  },
}));

const icon = <CheckBoxOutlineBlankIcon fontSize="small" />;
const checkedIcon = <CheckBoxIcon fontSize="small" />;

function nextId(): string {
  return `${Date.now()}-${Math.random().toString(16).slice(2)}`;
}

const AiDeckStudio: React.FC = () => {
  const apolloClient = useApolloClient();
  const { user } = useAccount();
  const theme = useTheme();
  const { collapsed: sidebarCollapsed } = useSidebar();
  const chatEndRef = useRef<HTMLDivElement | null>(null);
  const {
    shopifyConnectorId,
    facebookConnectorId,
    googleAdsConnectorId,
    googleAnalytics4ConnectorId,
    clientAccountId,
    propertyId,
  } = useBackendConnectors();

  const sidebarOffsetSx = sidebarCollapsed
    ? {
        [theme.breakpoints.up('sm')]: {
          marginLeft: '72px !important',
          width: 'calc(100% - 72px) !important',
        },
      }
    : {};

  const { data: reportsData, loading: loadingReports } = useQuery(
    GET_CUSTOM_REPORTS,
    {
      fetchPolicy: 'cache-first',
    },
  );
  const { data: kpiData, loading: loadingKpi } = useQuery(GET_KPI_SECTIONS, {
    fetchPolicy: 'cache-first',
  });

  const reports = useMemo(
    () => (reportsData?.getCustomReports ?? []) as CustomReport[],
    [reportsData],
  );
  const kpiSections = useMemo(
    () => (kpiData?.getKpiSections ?? []) as KpiSection[],
    [kpiData],
  );

  const [messages, setMessages] = useState<ChatMessage[]>(() => [
    {
      id: 'welcome',
      role: 'assistant',
      content:
        'Pick saved Custom reports and KPI sections — we pull their stored data into the export. ' +
        'Tell me who the audience is and what to stress. Then tap “Generate export prompt” to copy the block.',
    },
  ]);
  const [draft, setDraft] = useState('');
  const [selectedReports, setSelectedReports] = useState<CustomReport[]>([]);
  const [selectedKpiSections, setSelectedKpiSections] = useState<KpiSection[]>(
    [],
  );
  const [numSlides, setNumSlides] = useState<number>(10);
  const [promptText, setPromptText] = useState<string | null>(null);
  const [apiHintJson, setApiHintJson] = useState<string | null>(null);
  const [snack, setSnack] = useState<string | null>(null);
  const [isBuildingPrompt, setIsBuildingPrompt] = useState(false);
  const [deckGenerationResult, setDeckGenerationResult] = useState<{
    message: string;
    deckUrl?: string;
    deckCreditsRemaining?: number;
  } | null>(null);

  const loading = loadingReports || loadingKpi;

  useEffect(() => {
    chatEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  }, [messages]);

  const userChatText = useMemo(
    () =>
      messages
        .filter((m) => m.role === 'user')
        .map((m) => m.content)
        .join('\n'),
    [messages],
  );

  const inferredLanguage = useMemo(
    () =>
      inferDeckLanguageFromChat(
        userChatText,
        typeof navigator !== 'undefined' ? navigator.language : undefined,
      ),
    [userChatText],
  );

  const contextSummary = useMemo(() => {
    const reportNames = selectedReports
      .map((report) => report.name)
      .slice(0, 2);
    const sectionNames = selectedKpiSections
      .map((section) => section.name)
      .slice(0, 2);

    const parts = [
      `${selectedReports.length} custom report${selectedReports.length === 1 ? '' : 's'}`,
      `${selectedKpiSections.length} KPI section${selectedKpiSections.length === 1 ? '' : 's'}`,
      `${numSlides} slide${numSlides === 1 ? '' : 's'} max`,
    ];

    const labels: string[] = [];
    if (reportNames.length > 0)
      labels.push(`Reports: ${reportNames.join(', ')}`);
    if (sectionNames.length > 0)
      labels.push(`KPI sections: ${sectionNames.join(', ')}`);

    return {
      headline: `Context loaded: ${parts.join(' · ')}`,
      labels,
    };
  }, [numSlides, selectedKpiSections, selectedReports]);

  const reportsMissingPromptSnapshot = useMemo(
    () =>
      selectedReports.filter((report) => {
        const config =
          report.config && typeof report.config === 'object'
            ? (report.config as Record<string, unknown>)
            : null;
        return !config?.promptSnapshot;
      }),
    [selectedReports],
  );

  const sendUserMessage = useCallback(() => {
    const t = draft.trim();
    if (!t) return;
    setDraft('');
    setMessages((prev) => [
      ...prev,
      { id: nextId(), role: 'user', content: t },
    ]);
  }, [draft]);

  const handleGenerate = useCallback(async () => {
    if (selectedReports.length === 0 && selectedKpiSections.length === 0) {
      setSnack('Select at least one Custom report or KPI section.');
      return;
    }

    try {
      setIsBuildingPrompt(true);
      const kpiSectionBlocks = await buildKpiSectionPromptBlocks({
        client: apolloClient,
        runtime: {
          shopifyConnectorId,
          facebookConnectorId,
          googleAdsConnectorId,
          googleAnalytics4ConnectorId,
          clientAccountId,
          propertyId,
        },
        sections: selectedKpiSections,
      });

      const transcript = userChatText;
      const built = buildStudioExportPrompt({
        numSlides: numSlides,
        deckLanguageLabel: inferredLanguage,
        customReports: selectedReports,
        kpiSections: selectedKpiSections,
        kpiSectionBlocks,
        chatTranscript: transcript,
      });

      setPromptText(built);
      setDeckGenerationResult(null);
      setApiHintJson(
        JSON.stringify(
          buildDevApiHintJson({
            numSlides,
            deckLanguageLabel: inferredLanguage,
            customReportIds: selectedReports.map((r) => r.id),
            kpiSectionIds: selectedKpiSections.map((s) => s.id),
          }),
          null,
          2,
        ),
      );

      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'assistant',
          content:
            'I prepared the final prompt and I am generating the deck now...',
        },
      ]);

      const deckResponse = await apolloClient.mutate({
        mutation: GENERATE_DECK,
        variables: {
          input: {
            prompt: built,
            numSlides,
            creditsToConsume: 1,
          },
        },
      });

      const result = deckResponse?.data?.generateDeck;
      if (!result?.success) {
        setSnack(result?.message || 'Deck generation failed.');
        setMessages((prev) => [
          ...prev,
          {
            id: nextId(),
            role: 'assistant',
            content:
              result?.message ||
              'Deck generation failed. You can still copy the prompt and run it manually.',
          },
        ]);
        return;
      }

      setDeckGenerationResult({
        message: result.message,
        deckUrl: result.deckUrl || undefined,
        deckCreditsRemaining:
          typeof result.deckCreditsRemaining === 'number'
            ? result.deckCreditsRemaining
            : undefined,
      });

      setMessages((prev) => [
        ...prev,
        {
          id: nextId(),
          role: 'assistant',
          content: result.deckUrl
            ? `Deck generated successfully. Open: ${result.deckUrl}`
            : 'Deck generated successfully.',
        },
      ]);
    } catch {
      setSnack('Unable to build the KPI section snapshot right now.');
    } finally {
      setIsBuildingPrompt(false);
    }
  }, [
    apolloClient,
    clientAccountId,
    facebookConnectorId,
    googleAdsConnectorId,
    googleAnalytics4ConnectorId,
    inferredLanguage,
    numSlides,
    propertyId,
    selectedReports,
    selectedKpiSections,
    shopifyConnectorId,
    userChatText,
  ]);

  const copy = useCallback(async (value: string, label: string) => {
    try {
      await navigator.clipboard.writeText(value);
      setSnack(`Copied: ${label}`);
    } catch {
      setSnack('Copy failed — select text manually');
    }
  }, []);

  const canAccessDeckStudio = Boolean(user?.deckStudioEnabled);

  if (!canAccessDeckStudio) {
    return (
      <PageRoot sx={sidebarOffsetSx}>
        <Box sx={{ position: 'relative', maxWidth: 900, mx: 'auto', pt: 8 }}>
          <Card
            elevation={0}
            sx={{
              borderRadius: 3,
              border: '1px solid rgba(15, 23, 42, 0.08)',
              boxShadow: '0 20px 50px -24px rgba(15, 23, 42, 0.25)',
            }}
          >
            <CardContent sx={{ p: 3 }}>
              <Typography variant="h5" sx={{ fontWeight: 800, mb: 1 }}>
                Deck Studio access disabled
              </Typography>
              <Typography color="text.secondary">
                Ask your admin to enable `Deck Studio` for your user from the
                admin panel.
              </Typography>
            </CardContent>
          </Card>
        </Box>
      </PageRoot>
    );
  }

  return (
    <PageRoot sx={sidebarOffsetSx}>
      <Box
        sx={{
          position: 'absolute',
          top: -80,
          right: -60,
          width: 320,
          height: 320,
          borderRadius: '50%',
          background: `radial-gradient(circle, ${palette.primary.main}22 0%, transparent 70%)`,
          animation: `${drift} 12s ease-in-out infinite`,
          pointerEvents: 'none',
        }}
      />

      <Box sx={{ position: 'relative', maxWidth: 1100, mx: 'auto' }}>
        <Box
          sx={{
            display: 'inline-flex',
            alignItems: 'center',
            gap: 1,
            px: 1.5,
            py: 0.5,
            borderRadius: 999,
            bgcolor: 'rgba(30, 237, 159, 0.12)',
            border: '1px solid',
            borderColor: `${palette.primary.main}44`,
            mb: 1.5,
          }}
        >
          <AutoAwesomeIcon sx={{ fontSize: 18, color: palette.primary.main }} />
          <Typography
            variant="caption"
            sx={{
              fontWeight: 700,
              letterSpacing: 1,
              color: palette.primary.main,
            }}
          >
            LAB · Studio deck
          </Typography>
        </Box>

        <Typography
          variant="h3"
          sx={{
            fontWeight: 800,
            letterSpacing: '-0.03em',
            lineHeight: 1.15,
            mb: 1,
            fontSize: { xs: '1.65rem', sm: '2.05rem' },
            background: `linear-gradient(120deg, #0f172a 0%, #334155 45%, ${palette.primary.main} 160%)`,
            backgroundClip: 'text',
            WebkitBackgroundClip: 'text',
            WebkitTextFillColor: 'transparent',
            color: 'transparent',
          }}
        >
          From metrics to slides.
        </Typography>
        <Typography
          variant="body1"
          color="text.secondary"
          sx={{ mb: 2.5, maxWidth: 560, lineHeight: 1.6 }}
        >
          Chat-first workflow: define audience, storyline, and constraints in
          chat. Use setup controls only for sources and slide count.
        </Typography>

        {loading ? (
          <Box sx={{ display: 'flex', justifyContent: 'center', py: 6 }}>
            <CircularProgress />
          </Box>
        ) : (
          <Box
            sx={{
              display: 'grid',
              gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' },
              gap: 2,
              alignItems: 'stretch',
            }}
          >
            {/* Chat + sources */}
            <Card
              elevation={0}
              sx={{
                borderRadius: 3,
                border: '1px solid',
                borderColor: 'rgba(15, 23, 42, 0.08)',
                boxShadow: '0 20px 50px -24px rgba(15, 23, 42, 0.25)',
                display: 'flex',
                flexDirection: 'column',
                minHeight: 520,
              }}
            >
              <CardContent
                sx={{
                  flex: 1,
                  display: 'flex',
                  flexDirection: 'column',
                  gap: 2,
                  p: { xs: 2, sm: 2.5 },
                }}
              >
                <Box
                  sx={{
                    flex: 1,
                    overflowY: 'auto',
                    maxHeight: { xs: 280, sm: 320 },
                    pr: 0.5,
                    display: 'flex',
                    flexDirection: 'column',
                    gap: 1.25,
                  }}
                >
                  {messages.map((m) => (
                    <Box
                      key={m.id}
                      sx={{
                        alignSelf:
                          m.role === 'user' ? 'flex-end' : 'flex-start',
                        maxWidth: '92%',
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.5,
                          py: 1,
                          borderRadius: 2,
                          bgcolor:
                            m.role === 'user'
                              ? `${palette.primary.main}28`
                              : 'rgba(15,23,42,0.06)',
                        }}
                      >
                        <Typography
                          variant="body2"
                          sx={{ lineHeight: 1.55, color: 'text.primary' }}
                        >
                          {m.content}
                        </Typography>
                      </Box>
                    </Box>
                  ))}
                  <Box sx={{ alignSelf: 'flex-start', maxWidth: '92%' }}>
                    <Box
                      sx={{
                        px: 1.5,
                        py: 1.1,
                        borderRadius: 2,
                        bgcolor: 'rgba(15,23,42,0.04)',
                        border: '1px dashed rgba(15,23,42,0.1)',
                      }}
                    >
                      <Typography
                        variant="body2"
                        sx={{ lineHeight: 1.55, color: 'text.primary' }}
                      >
                        {contextSummary.headline}
                      </Typography>
                      {contextSummary.labels.map((label) => (
                        <Typography
                          key={label}
                          variant="caption"
                          sx={{
                            display: 'block',
                            mt: 0.4,
                            color: 'text.secondary',
                          }}
                        >
                          {label}
                        </Typography>
                      ))}
                    </Box>
                  </Box>
                  <div ref={chatEndRef} />
                </Box>

                <Box sx={{ display: 'flex', gap: 1, alignItems: 'flex-start' }}>
                  <TextField
                    fullWidth
                    multiline
                    maxRows={3}
                    size="small"
                    placeholder="Audience, tone, key story, what to highlight..."
                    value={draft}
                    onChange={(e) => setDraft(e.target.value)}
                    onKeyDown={(e) => {
                      if (e.key === 'Enter' && !e.shiftKey) {
                        e.preventDefault();
                        sendUserMessage();
                      }
                    }}
                    sx={{
                      '& .MuiOutlinedInput-root': {
                        borderRadius: 2,
                        bgcolor: 'rgba(255,255,255,0.92)',
                      },
                      '& .MuiInputBase-input::placeholder': {
                        color: 'text.secondary',
                        opacity: 0.95,
                      },
                    }}
                  />
                  <Button
                    variant="contained"
                    onClick={sendUserMessage}
                    sx={{
                      minWidth: 48,
                      px: 1.5,
                      borderRadius: 2,
                      bgcolor: palette.primary.main,
                      color: '#0f172a',
                      '&:hover': { filter: 'brightness(0.94)' },
                    }}
                  >
                    <SendIcon fontSize="small" />
                  </Button>
                </Box>

                <Accordion
                  disableGutters
                  elevation={0}
                  defaultExpanded={false}
                  sx={{
                    borderRadius: 2.5,
                    border: '1px solid rgba(15,23,42,0.08)',
                    bgcolor: 'rgba(248,250,252,0.9)',
                    '&:before': { display: 'none' },
                    '& .MuiAccordionSummary-root:focus, & .MuiAccordionSummary-root:focus-visible':
                      {
                        outline: 'none',
                      },
                    '& .MuiAccordionSummary-root.Mui-focusVisible': {
                      backgroundColor: 'transparent',
                    },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 700, letterSpacing: 0.2 }}
                    >
                      Deck setup (sources and slides)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails sx={{ display: 'grid', gap: 1.25, pt: 0 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        gap: 0.75,
                        flexWrap: 'wrap',
                      }}
                    >
                      <Box
                        sx={{
                          px: 1.1,
                          py: 0.45,
                          borderRadius: 999,
                          bgcolor: 'rgba(15,23,42,0.06)',
                          border: '1px solid rgba(15,23,42,0.1)',
                          fontSize: '0.76rem',
                          fontWeight: 600,
                          color: 'text.secondary',
                        }}
                      >
                        {selectedReports.length} report
                        {selectedReports.length === 1 ? '' : 's'}
                      </Box>
                      <Box
                        sx={{
                          px: 1.1,
                          py: 0.45,
                          borderRadius: 999,
                          bgcolor: 'rgba(15,23,42,0.06)',
                          border: '1px solid rgba(15,23,42,0.1)',
                          fontSize: '0.76rem',
                          fontWeight: 600,
                          color: 'text.secondary',
                        }}
                      >
                        {selectedKpiSections.length} KPI section
                        {selectedKpiSections.length === 1 ? '' : 's'}
                      </Box>
                      <Box
                        sx={{
                          px: 1.1,
                          py: 0.45,
                          borderRadius: 999,
                          bgcolor: 'rgba(30, 237, 159, 0.15)',
                          border: `1px solid ${palette.primary.main}66`,
                          fontSize: '0.76rem',
                          fontWeight: 700,
                          color: 'text.primary',
                        }}
                      >
                        {numSlides} slides
                      </Box>
                    </Box>
                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={reports}
                      getOptionLabel={(o) => o.name || 'Untitled'}
                      value={selectedReports}
                      onChange={(_, v) => setSelectedReports(v)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={selected}
                            sx={{ mr: 1 }}
                          />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select saved reports"
                          size="small"
                        />
                      )}
                    />
                    {selectedReports.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No custom reports selected yet.
                      </Typography>
                    )}

                    {reportsMissingPromptSnapshot.length > 0 && (
                      <Typography
                        variant="caption"
                        sx={{ color: palette.warning.main, lineHeight: 1.5 }}
                      >
                        Some selected reports were saved before export snapshots
                        were available. Open them in Custom Report and save once
                        to export the full table.
                      </Typography>
                    )}

                    <Autocomplete
                      multiple
                      disableCloseOnSelect
                      options={kpiSections}
                      getOptionLabel={(o) => o.name || 'Untitled'}
                      value={selectedKpiSections}
                      onChange={(_, v) => setSelectedKpiSections(v)}
                      renderOption={(props, option, { selected }) => (
                        <li {...props}>
                          <Checkbox
                            icon={icon}
                            checkedIcon={checkedIcon}
                            checked={selected}
                            sx={{ mr: 1 }}
                          />
                          {option.name}
                        </li>
                      )}
                      renderInput={(params) => (
                        <TextField
                          {...params}
                          placeholder="Select saved sections"
                          size="small"
                        />
                      )}
                    />
                    {selectedKpiSections.length === 0 && (
                      <Typography variant="caption" color="text.secondary">
                        No KPI sections selected yet.
                      </Typography>
                    )}

                    <Box
                      sx={{
                        display: 'grid',
                        gridTemplateColumns: '1fr',
                        gap: 1,
                        alignItems: 'end',
                      }}
                    >
                      <Box>
                        <Typography
                          id="studio-deck-slide-count-label"
                          variant="caption"
                          component="label"
                          htmlFor="studio-deck-slide-count"
                          color="text.secondary"
                          sx={{ fontWeight: 600, display: 'block', mb: 0.75 }}
                        >
                          Output: presentation only
                        </Typography>
                        <FormControl
                          size="small"
                          fullWidth
                          sx={{
                            '& .MuiOutlinedInput-root': {
                              borderRadius: 2,
                              bgcolor: 'rgba(255,255,255,0.95)',
                            },
                            '& .MuiSelect-select': {
                              color: 'text.primary',
                              fontWeight: 700,
                              fontSize: '1rem',
                              py: 1.25,
                              lineHeight: 1.3,
                            },
                          }}
                        >
                          <Select
                            id="studio-deck-slide-count"
                            value={numSlides}
                            inputProps={{
                              'aria-labelledby':
                                'studio-deck-slide-count-label',
                            }}
                            onChange={(e) =>
                              setNumSlides(Number(e.target.value))
                            }
                          >
                            {SLIDE_OPTIONS.map((n) => (
                              <MenuItem
                                key={n}
                                value={n}
                                sx={{ color: 'text.primary', fontWeight: 600 }}
                              >
                                {n} slides
                              </MenuItem>
                            ))}
                          </Select>
                        </FormControl>
                      </Box>
                    </Box>
                  </AccordionDetails>
                </Accordion>

                <Button
                  variant="contained"
                  size="large"
                  fullWidth
                  startIcon={<AutoAwesomeIcon />}
                  onClick={handleGenerate}
                  disabled={isBuildingPrompt}
                  sx={{
                    textTransform: 'none',
                    fontWeight: 800,
                    fontSize: '1rem',
                    borderRadius: 2,
                    py: 1.35,
                    background: `linear-gradient(135deg, ${palette.primary.main} 0%, #22c55e 100%)`,
                    color: '#0f172a',
                    boxShadow: `0 12px 32px -8px ${palette.primary.main}88`,
                    '&:hover': {
                      boxShadow: `0 16px 40px -10px ${palette.primary.main}aa`,
                      filter: 'brightness(1.03)',
                    },
                  }}
                >
                  {isBuildingPrompt
                    ? 'Building export prompt...'
                    : 'Generate export prompt'}
                </Button>
              </CardContent>
            </Card>

            <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
              {promptText && (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'rgba(15, 23, 42, 0.1)',
                    boxShadow: '0 16px 40px -20px rgba(15, 23, 42, 0.2)',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Box
                      sx={{
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'space-between',
                        mb: 1.5,
                        flexWrap: 'wrap',
                        gap: 1,
                      }}
                    >
                      <Typography variant="subtitle1" sx={{ fontWeight: 800 }}>
                        AI export prompt
                      </Typography>
                      <Button
                        size="small"
                        variant="contained"
                        startIcon={<ContentCopyIcon />}
                        onClick={() => copy(promptText, 'prompt')}
                        sx={{
                          textTransform: 'none',
                          fontWeight: 700,
                          borderRadius: 2,
                        }}
                      >
                        Copy all
                      </Button>
                    </Box>
                    <TextField
                      multiline
                      fullWidth
                      minRows={16}
                      value={promptText}
                      InputProps={{ readOnly: true }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          borderRadius: 2,
                          bgcolor: '#0f172a08',
                        },
                        '& .MuiInputBase-input': {
                          fontFamily: 'ui-monospace, SFMono-Regular, monospace',
                          fontSize: '0.78rem',
                          lineHeight: 1.5,
                          color: 'text.primary',
                        },
                      }}
                    />
                  </CardContent>
                </Card>
              )}

              {deckGenerationResult && (
                <Card
                  elevation={0}
                  sx={{
                    borderRadius: 3,
                    border: '1px solid',
                    borderColor: 'rgba(15, 23, 42, 0.1)',
                    boxShadow: '0 16px 40px -20px rgba(15, 23, 42, 0.2)',
                  }}
                >
                  <CardContent sx={{ p: 2.5 }}>
                    <Typography
                      variant="subtitle1"
                      sx={{ fontWeight: 800, mb: 1 }}
                    >
                      Deck generation
                    </Typography>
                    <Typography variant="body2" sx={{ mb: 1 }}>
                      {deckGenerationResult.message}
                    </Typography>
                    {deckGenerationResult.deckUrl && (
                      <Typography
                        variant="body2"
                        sx={{ wordBreak: 'break-all', mb: 1 }}
                      >
                        {deckGenerationResult.deckUrl}
                      </Typography>
                    )}
                    {typeof deckGenerationResult.deckCreditsRemaining ===
                      'number' && (
                      <Typography variant="caption" color="text.secondary">
                        Credits remaining:{' '}
                        {deckGenerationResult.deckCreditsRemaining}
                      </Typography>
                    )}
                  </CardContent>
                </Card>
              )}

              {apiHintJson && (
                <Accordion
                  disableGutters
                  elevation={0}
                  sx={{
                    borderRadius: 2,
                    border: '1px solid',
                    borderColor: 'rgba(15,23,42,0.08)',
                    '&:before': { display: 'none' },
                  }}
                >
                  <AccordionSummary expandIcon={<ExpandMoreIcon />}>
                    <Typography
                      variant="caption"
                      color="text.secondary"
                      sx={{ fontWeight: 600 }}
                    >
                      Developer payload (future API)
                    </Typography>
                  </AccordionSummary>
                  <AccordionDetails>
                    <Button
                      size="small"
                      startIcon={<ContentCopyIcon />}
                      onClick={() => copy(apiHintJson, 'JSON')}
                      sx={{ textTransform: 'none', mb: 1 }}
                    >
                      Copy JSON
                    </Button>
                    <TextField
                      multiline
                      fullWidth
                      minRows={8}
                      value={apiHintJson}
                      InputProps={{ readOnly: true }}
                      size="small"
                      sx={{
                        '& .MuiInputBase-input': {
                          fontFamily: 'monospace',
                          fontSize: '0.7rem',
                        },
                      }}
                    />
                  </AccordionDetails>
                </Accordion>
              )}
            </Box>
          </Box>
        )}
      </Box>

      <Snackbar
        open={!!snack}
        autoHideDuration={3000}
        onClose={() => setSnack(null)}
        message={snack}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      />
    </PageRoot>
  );
};

export default AiDeckStudio;
